import {
  Box,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
} from '@betterleap/ui';
import { useState } from 'react';
import { Location, NavigateFunction } from 'react-router-dom';
import logo from '../../../assets/images/logo.png';
import SidebarItem from '../SidebarItem/SidebarItem';
import SidebarLink from '../SidebarLink/SidebarLink';
import styles from './MobileSidebar.module.scss';

interface SidebarProps {
  navBar?: JSX.Element;
  subHeader?: JSX.Element;
  logoOverride?: JSX.Element;
  displayIcon?: boolean;
  side?: 'right' | 'left';
  hideAt?: 'xs:hidden' | 'sm:hidden' | 'md:hidden' | 'lg:hidden';
  sidebarRoutes: {
    title: string;
    route?: string;
    disabled?: boolean;
    icon?: string | JSX.Element;
    onClick?: () => void;
    active?: boolean;
    beta?: boolean;
    hidden?: boolean;
    subRoutes?: {
      title: string;
      route: string;
    }[];
  }[];
  sidebarFooterRoutes?: {
    title: string;
    route?: string;
    disabled?: boolean;
    icon?: string | JSX.Element;
    onClick?: () => void;
    active?: boolean;
    beta?: boolean;
    hidden?: boolean;
  }[];
  footerBanner?: React.ReactNode;
  handleLogout?: () => void;
  handleNavigate?: NavigateFunction | ((url: string) => void);
  currentLocation?: Location | string;
}

const MobileSidebar = ({
  navBar,
  subHeader,
  logoOverride,
  displayIcon = true,
  side = 'right',
  hideAt = 'lg:hidden',
  sidebarRoutes,
  sidebarFooterRoutes = [],
  handleLogout,
  handleNavigate,
  footerBanner,
  currentLocation,
}: SidebarProps) => {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <>
      <div className={`mr-5 ${hideAt} overflowy-scroll`}>
        <Flex css={{ gap: '10px' }}>
          {navBar}
          <span onClick={() => setOpen(true)}>
            <Flex>
              <Icon name='menu' className='cursor-pointer' />
            </Flex>
          </span>
        </Flex>
      </div>
      <div
        className='fixed w-full h-full top-0'
        style={{
          zIndex: 100000,
          ...(open ? { visibility: 'visible' } : { visibility: 'hidden' }),
        }}
      >
        <div
          className='w-full h-full'
          onClick={() => setOpen(false)}
          style={{ backgroundColor: 'rgba(0,0,0,.5)' }}
        ></div>
        <div
          className={
            open
              ? `fixed ${hideAt} ${side}-0 top-0 w-[80%] h-full bg-white ease-in-out duration-500 overflow-auto`
              : `ease-in-out w-[80%] duration-500 fixed top-0 bottom-0 right-[-100%]`
          }
        >
          <Box css={{ flex: 1 }}>
            <Flex
              css={{
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '1em',
              }}
            >
              {logoOverride ? (
                logoOverride
              ) : (
                <img src={logo} className={styles.logo} alt='logo' />
              )}
              <Flex
                onClick={() => setOpen(false)}
                css={{
                  height: 40,
                  width: 40,
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Icon name='x' />
              </Flex>
            </Flex>
            {subHeader && <>{subHeader}</>}
            {sidebarRoutes.map((item) =>
              item.route ? (
                <SidebarLink
                  key={item.title}
                  title={item.title}
                  route={item.route}
                  icon={displayIcon ? item.icon : undefined}
                  hidden={item.hidden}
                  handleNavigate={handleNavigate}
                  currentLocation={currentLocation}
                />
              ) : item.subRoutes ? (
                <Collapsible key={item.title} defaultOpen={true}>
                  <CollapsibleTrigger asChild className='cursor-pointer'>
                    <SidebarItem
                      title={item.title}
                      icon={displayIcon ? item.icon : undefined}
                      hidden={item.hidden}
                      collapsable={true}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div
                      className='px-5'
                      style={{
                        borderLeft: '1px solid #E8EAED',
                        padding: 0,
                        marginLeft: '1.5em',
                      }}
                    >
                      {item.subRoutes?.map((subRoute) => (
                        <SidebarLink
                          key={subRoute.title}
                          title={subRoute.title}
                          route={subRoute.route}
                          handleNavigate={handleNavigate}
                          currentLocation={currentLocation}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <SidebarItem
                  key={item.title}
                  title={item.title}
                  icon={displayIcon ? item.icon : undefined}
                  onClick={item.onClick}
                  hidden={item.hidden}
                />
              ),
            )}
          </Box>
          <div>
            <Box css={{ px: 16 }}>{footerBanner}</Box>
            {sidebarFooterRoutes.map((item) =>
              item.route ? (
                <SidebarLink
                  key={item.title}
                  title={item.title}
                  route={item.route}
                  icon={displayIcon ? item.icon : undefined}
                  hidden={item.hidden}
                  handleNavigate={handleNavigate}
                  currentLocation={currentLocation}
                />
              ) : (
                <SidebarItem
                  key={item.title}
                  title={item.title}
                  icon={displayIcon ? item.icon : undefined}
                  onClick={item.onClick}
                  hidden={item.hidden}
                />
              ),
            )}
            {!!handleLogout && (
              <SidebarItem
                css={{ mb: 4 }}
                icon={
                  <Icon name='logout-alt' size={20} color='$neutral-blue-700' />
                }
                title='Logout'
                onClick={handleLogout}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileSidebar;
